import {SchedulerEvent, SchedulerResource} from '@mui/x-scheduler/models';

import {
    CalendarEntryDto,
    CalendarTaskDto,
    CalendarViewDto,
    OutlookCalendarViewDto,
    OutlookEventDto,
} from 'apiClient/Api';

/**
 * Die Quellen des Kalenders als Ressourcen-Baum.
 *
 * Oberste Ebene ist die **Herkunft** (Labelwin-Plantafel, Microsoft 365), darunter bei Labelwin
 * die Kategorien. Der Kalender macht daraus eine Liste mit Häkchen: damit lässt sich jede Quelle
 * und jede Kategorie einzeln ein- und ausblenden — genau die Frage „was liegt wo?", ohne zwei
 * Kalender nebeneinander stellen zu müssen.
 *
 * Die Ids sind Vertrag: sie stehen im `resource`-Feld der Termine und dürfen nicht wandern.
 */
export const SOURCE_LABELWIN = 'labelwin';
export const SOURCE_O365 = 'o365';

/**
 * Die Rohdaten hinter einem Termin im Raster.
 *
 * Der Kalender kennt nur sein eigenes Termin-Modell (Titel, Zeit, Beschreibung). Für einen
 * Detail-Dialog mit anklickbarer Telefonnummer und Teams-Knopf braucht es die ursprünglichen
 * Felder — deshalb wird zu jedem Termin die Quelle mitgeführt, adressiert über dieselbe Id.
 */
export type CalendarEventDetail =
    | {kind: 'entry'; entry: CalendarEntryDto}
    | {kind: 'task'; task: CalendarTaskDto}
    | {kind: 'outlook'; event: OutlookEventDto};

/** Termine für das Raster samt ihrer Rohdaten. */
export interface MappedCalendar {
    events: SchedulerEvent[];
    /** Rohdaten je Termin-Id — die Grundlage des Detail-Dialogs. */
    details: Map<string, CalendarEventDetail>;
}

/** Kategorie-Ids unter Labelwin. Abgeleitet aus `CalendarEntryDto.kind` des Servers. */
export const LABELWIN_KINDS = {
    order: 'labelwin.order',
    appointment: 'labelwin.appointment',
    absence: 'labelwin.absence',
    project: 'labelwin.project',
    task: 'labelwin.task',
    other: 'labelwin.other',
} as const;

/**
 * Die Eintragsarten, die diese Oberfläche anlegen und ändern darf — der Wert ist die `belegart`.
 *
 * Dieselbe Liste setzt der Server durch (`WritableEntryKind`). Alles andere — Aufträge,
 * Projekttermine, der Aufgaben-Spiegel — verwaltet Labelwin und bleibt schreibgeschützt,
 * unabhängig davon, ob der Server Schreibzugriff erlaubt.
 */
export const WRITABLE_ENTRY_KINDS = {
    vacation: 0,
    sick: 1,
    training: 2,
    standby: 7,
    appointment: 100,
} as const;

/** Die anlegbaren Arten als Werteliste — Reihenfolge der Auswahl in der Oberfläche. */
export const WRITABLE_ENTRY_KIND_VALUES: number[] = [
    WRITABLE_ENTRY_KINDS.appointment,
    WRITABLE_ENTRY_KINDS.vacation,
    WRITABLE_ENTRY_KINDS.sick,
    WRITABLE_ENTRY_KINDS.training,
    WRITABLE_ENTRY_KINDS.standby,
];

/**
 * Darf dieser Eintrag über die Schnittstelle geändert werden?
 *
 * Genau die Regel des Servers: eine anlegbare Art, kein Auftragsbezug, kein mehrtägiger Balken.
 * Ein mehrtägiger Urlaub besteht aus einem Kopfsatz und je Werktag einem Tagessatz — den korrekt
 * zu verschieben hieße, die Arbeitszeitlogik des ERP nachzubilden.
 */
export const isWritableEntry = (entry: CalendarEntryDto): boolean =>
    // Ohne belegart ist die Art unbekannt — dann lieber nicht anfassen.
    entry.rawKind != null
    && WRITABLE_ENTRY_KIND_VALUES.includes(entry.rawKind)
    && entry.orderId == null
    && !entry.isPartOfSpan;

/** Baut den Ressourcen-Baum mit übersetzten Bezeichnungen. */
export const buildSources = (
    label: (key: string) => string,
    includeO365: boolean,
): SchedulerResource[] => {
    const sources: SchedulerResource[] = [
        {
            id: SOURCE_LABELWIN,
            title: label('calendar.sources.labelwin'),
            eventColor: 'teal',
            children: [
                {id: LABELWIN_KINDS.order, title: label('calendar.sources.order'), eventColor: 'teal'},
                {id: LABELWIN_KINDS.appointment, title: label('calendar.sources.appointment'), eventColor: 'green'},
                {id: LABELWIN_KINDS.absence, title: label('calendar.sources.absence'), eventColor: 'amber'},
                {id: LABELWIN_KINDS.project, title: label('calendar.sources.project'), eventColor: 'indigo'},
                {id: LABELWIN_KINDS.task, title: label('calendar.sources.task'), eventColor: 'grey'},
                {id: LABELWIN_KINDS.other, title: label('calendar.sources.other'), eventColor: 'grey'},
            ],
        },
    ];

    // Microsoft 365 erscheint erst, wenn es dafür auch Daten geben kann — eine Quelle, die
    // dauerhaft leer bleibt, sieht aus wie ein Fehler.
    if (includeO365) {
        sources.push({
            id: SOURCE_O365,
            title: label('calendar.sources.o365'),
            eventColor: 'blue',
            areEventsReadOnly: true,
        });
    }

    return sources;
};

/** Ordnet die Kategorie des Servers einer Ressource zu. */
const resourceForKind = (kind?: string | null): string => {
    switch (kind) {
        case 'Order':
            return LABELWIN_KINDS.order;
        case 'Appointment':
            return LABELWIN_KINDS.appointment;
        case 'Absence':
            return LABELWIN_KINDS.absence;
        case 'Project':
            return LABELWIN_KINDS.project;
        case 'Task':
            return LABELWIN_KINDS.task;
        default:
            return LABELWIN_KINDS.other;
    }
};

/**
 * Entfernt eine Zeitzonen-Angabe vom Zeitpunkt.
 *
 * Der Kalender liest Zeitpunkte **ohne** `Z` als Wanduhrzeit — dieselbe Semantik, in der Labelwin
 * seine Zeiten führt, und genau das liefert der Server (`WallClockDateTimeConverter`). Das
 * Abschneiden ist die Absicherung für den Fall, dass doch einmal ein `Z` mitkommt: sonst
 * verschiebt der Kalender den Termin im Sommer um zwei Stunden — lautlos.
 */
export const wallClock = (value: string): string => value.replace(/(Z|[+-]\d{2}:\d{2})$/, '');

/** Der Tagesanteil eines Zeitpunkts oder Datums. */
const dayOf = (value?: string | null): string | undefined =>
    value ? wallClock(value).slice(0, 10) : undefined;

/**
 * Sammelt beschriftete Zeilen und lässt leere Werte weg.
 *
 * Labelwin pflegt die Felder nicht durchgängig (laut LabelConnect-Doku tragen 46 von 131
 * Einträgen eine Anmerkung, aber nur 10 eine Support-Id). Eine Zeile „Support-Id: —" wäre
 * Rauschen — deshalb erscheint jede Angabe nur, wenn sie da ist.
 */
class Lines {
    private readonly out: string[] = [];

    constructor(private readonly label: (key: string) => string) {
    }

    /** Beschriftete Zeile, wenn der Wert vorhanden ist. */
    add(key: string, value?: string | number | null): this {
        if (value !== null && value !== undefined && `${value}`.trim().length > 0) {
            this.out.push(`${this.label(key)}: ${value}`);
        }
        return this;
    }

    /** Freitext-Absatz mit Überschrift — für mehrzeilige Felder wie Anmerkung oder Fehlerbild. */
    paragraph(key: string, value?: string | null): this {
        if (value && value.trim().length > 0) {
            this.out.push('', `${this.label(key)}:`, value.trim());
        }
        return this;
    }

    build(): string | undefined {
        const text = this.out.join('\n').trim();
        return text.length > 0 ? text : undefined;
    }
}

/**
 * Die Beschreibung eines Labelwin-Eintrags — der Inhalt, den der Detail-Dialog zeigt.
 *
 * Der Betreff trägt kaum Information: er ist in Projdat auf 50 Zeichen begrenzt und enthält meist
 * nur ein Kürzel („GÖTTINGEN,ELW"). Was tatsächlich zu tun ist, steht in der Anmerkung und am
 * Auftrag — deshalb wandert das hier vollständig hinein.
 */
const describeEntry = (entry: CalendarEntryDto, label: (key: string) => string): string | undefined => {
    const lines = new Lines(label);
    const order = entry.order;

    lines
        .add('calendar.field.order', order?.number ?? entry.orderNumber)
        .add('calendar.field.orderSubject', order?.subject)
        .add('calendar.field.customer', [order?.customerName, order?.customerCity]
            .filter(Boolean).join(', ') || undefined)
        .add('calendar.field.system', order?.systemName)
        .add('calendar.field.supportId', order?.supportId)
        .add('calendar.field.contract', order?.contractNumber)
        .add('calendar.field.contact', [order?.contactName, order?.contactPhone, order?.contactMail]
            .filter(Boolean).join(' · ') || undefined)
        .add('calendar.field.channel', order?.channel)
        .add('calendar.field.priority', order?.priority)
        .add('calendar.field.project', entry.project)
        .add('calendar.field.orderProject', entry.orderProject)
        .add('calendar.field.hours', entry.hours)
        .add('calendar.field.plannedHours', order?.plannedHours)
        .paragraph('calendar.field.note', entry.note)
        .paragraph('calendar.field.problem', order?.comment);

    // Der Tooltip wiederholt häufig, was oben schon steht — nur zeigen, wenn er eigenständig ist.
    if (entry.tooltip && entry.tooltip !== order?.subject && entry.tooltip !== entry.note) {
        lines.paragraph('calendar.field.tooltip', entry.tooltip);
    }

    return lines.build();
};

/** Die Beschreibung einer Wiedervorlage. */
const describeTask = (task: CalendarTaskDto, label: (key: string) => string): string | undefined =>
    new Lines(label)
        .add('calendar.field.activity', task.activity)
        .add('calendar.field.status', task.status)
        .add('calendar.field.createdBy', task.createdBy)
        .add('calendar.field.priority', task.priority)
        .build();

/** Die Beschreibung eines Outlook-Termins. */
const describeOutlookEvent = (
    event: OutlookEventDto,
    label: (key: string) => string,
): string | undefined =>
    new Lines(label)
        .add('calendar.field.location', event.location)
        .add('calendar.field.organizer', [event.organizer, event.organizerMail]
            .filter(Boolean).join(' · ') || undefined)
        .add('calendar.field.attendees', event.attendeeCount ? event.attendeeCount : undefined)
        .add('calendar.field.showAs', event.showAs ? label(`calendar.showAs.${event.showAs}`) : undefined)
        .add('calendar.field.response', event.responseStatus
            ? label(`calendar.response.${event.responseStatus}`)
            : undefined)
        .add('calendar.field.meetingUrl', event.isOnlineMeeting ? event.onlineMeetingUrl : undefined)
        .paragraph('calendar.field.preview', event.preview)
        .build();

/**
 * Bringt die Antwort des Servers auf das Termin-Modell des Kalenders.
 *
 * Zeitpunkte werden **unverändert als Wanduhrzeit** übernommen. Einträge ohne verwertbare
 * Zeitspanne (Wiedervorlagen ohne Uhrzeit, mehrtägige Balken) werden als ganztägig geführt —
 * ihnen eine Uhrzeit anzudichten, die Labelwin nicht hat, wäre eine Erfindung.
 */
export const toSchedulerEvents = (
    view: CalendarViewDto | undefined,
    label: (key: string) => string,
): MappedCalendar => {
    const events: SchedulerEvent[] = [];
    const details = new Map<string, CalendarEventDetail>();

    for (const entry of view?.entries ?? []) {
        const day = dayOf(entry.date) ?? dayOf(entry.start);
        if (!day) continue;

        const start = entry.start ? wallClock(entry.start) : undefined;
        const end = entry.end ? wallClock(entry.end) : undefined;
        const allDay = (entry.isAllDay ?? false) || !start || !end || end <= start;

        const id = `e${entry.id}`;
        details.set(id, {kind: 'entry', entry});
        events.push({
            id,
            title: entry.subject?.trim() || '—',
            description: describeEntry(entry, label),
            start: allDay ? `${day}T00:00:00` : start!,
            end: allDay ? `${day}T23:59:00` : end!,
            allDay,
            resource: resourceForKind(entry.kind),
            // Freie Termine und einzelne Abwesenheitstage sind änderbar — dieselbe Regel, die der
            // Server durchsetzt. Aufträge, Projekttermine und mehrtägige Balken gehören Labelwin.
            readOnly: !isWritableEntry(entry),
        });
    }

    for (const task of view?.tasks ?? []) {
        const day = dayOf(task.date);
        if (!day) continue;

        const start = task.start ? wallClock(task.start) : undefined;
        const end = task.end ? wallClock(task.end) : undefined;
        const allDay = !start || !end || end <= start;

        const id = `t${task.id}`;
        details.set(id, {kind: 'task', task});
        events.push({
            id,
            title: task.subject?.trim() || '—',
            description: describeTask(task, label),
            start: allDay ? `${day}T00:00:00` : start!,
            end: allDay ? `${day}T23:59:00` : end!,
            allDay,
            resource: LABELWIN_KINDS.task,
            // Wiedervorlagen verwaltet Labelwin.
            readOnly: true,
        });
    }

    return {events, details};
};

/**
 * Bringt die Outlook-Termine auf dasselbe Modell.
 *
 * Zeitpunkte kommen als Wanduhrzeit in der Zeitzone des Kalenders — Microsoft Graph rechnet
 * serverseitig um (siehe `O365GraphClient`). Damit liegen sie ohne eigene Rechnung im gleichen
 * Raster wie die Labelwin-Termine.
 *
 * Abgesagte Termine bleiben draußen: sie stehen in Outlook noch im Kalender, blockieren aber
 * keine Zeit — im Raster wären sie irreführend.
 */
export const toOutlookEvents = (
    outlook: OutlookCalendarViewDto | undefined,
    label: (key: string) => string,
): MappedCalendar => {
    const events: SchedulerEvent[] = [];
    const details = new Map<string, CalendarEventDetail>();

    for (const event of outlook?.entries ?? []) {
        if (!event.id || !event.start || event.isCancelled) continue;

        const start = wallClock(event.start);
        const end = event.end ? wallClock(event.end) : undefined;
        const day = start.slice(0, 10);
        const allDay = (event.isAllDay ?? false) || !end || end <= start;

        const id = `o${event.id}`;
        details.set(id, {kind: 'outlook', event});
        events.push({
            id,
            title: event.subject?.trim() || label('calendar.noSubject'),
            description: describeOutlookEvent(event, label),
            start: allDay ? `${day}T00:00:00` : start,
            end: allDay ? `${day}T23:59:00` : end!,
            allDay,
            resource: SOURCE_O365,
            // Outlook-Termine sind Lesestoff: der Schreibpfad nach Microsoft 365 ist nicht gebaut
            // (dafür bräuchte die App-Registrierung Calendars.ReadWrite).
            readOnly: true,
            // „Frei" markierte Termine blockieren keine Zeit — blass statt satt, damit sie im
            // Raster nicht wie ein Termin aussehen, den man nicht überplanen darf.
            color: event.showAs === 'free' ? 'grey' : undefined,
        });
    }

    return {events, details};
};
